import { DataTable } from '@/components/data-table/DataTable';
import { PageHeader } from '@/components/feedback/PageHeader';
import { PageLoader } from '@/components/feedback/PageLoader';
import { FillPage } from '@/components/layout/PageContainer';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { prettyJson } from '@/features/tts-flow/lib/diff-helper';
import { useUserModules } from '@/features/voice-campaigns/api';
import { formatDate, formatDateTime } from '@/lib/utils/date';
import type { ColumnDef } from '@tanstack/react-table';
import type { LucideIcon } from 'lucide-react';
import {
  ArrowLeft,
  CheckCircle2,
  ClipboardList,
  Clock,
  Code,
  Database,
  FileUp,
  Loader2,
  RefreshCw,
  XCircle,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import type { ContinuousBaseUploadLogRow } from '../api';
import { useContinuousBaseUploadLogs } from '../api';

const ACTION_META: Record<number, { label: string; icon: LucideIcon }> = {
  0: { label: 'File upload', icon: FileUp },
  1: { label: 'Existing list', icon: Database },
  2: { label: 'Pasted numbers', icon: ClipboardList },
};

function ActionBadge({ action }: { action: number }) {
  const meta = ACTION_META[action] ?? { label: `Action ${action}`, icon: Code };
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className="gap-1 font-normal">
      <Icon className="h-3 w-3" />
      {meta.label}
    </Badge>
  );
}

const STATUS_META: Record<
  string,
  { label: string; icon: LucideIcon; spin?: boolean; className: string }
> = {
  queued: {
    label: 'Queued',
    icon: Clock,
    className: 'border-sky-500/25 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  },
  processing: {
    label: 'Processing',
    icon: Loader2,
    spin: true,
    className: 'border-amber-500/25 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  },
  complete: {
    label: 'Complete',
    icon: CheckCircle2,
    className: 'border-emerald-500/25 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400',
  },
  error: {
    label: 'Error',
    icon: XCircle,
    className: 'border-rose-500/25 bg-rose-500/10 text-rose-700 dark:text-rose-400',
  },
};

function StatusBadge({ status }: { status: string }) {
  const meta =
    STATUS_META[status] ??
    ({
      label: status,
      icon: Clock,
      spin: false,
      className: 'border-border bg-muted text-muted-foreground',
    } as const);
  const Icon = meta.icon;
  return (
    <Badge variant="outline" className={`gap-1 font-normal capitalize ${meta.className}`}>
      <Icon className={`h-3 w-3 ${meta.spin ? 'animate-spin' : ''}`} />
      {meta.label}
    </Badge>
  );
}

function JsonPane({ title, json, tone }: { title: string; json: string; tone?: 'error' }) {
  return (
    <div className="min-w-0 space-y-1.5">
      <span
        className={`text-xs font-medium ${
          tone === 'error' ? 'text-rose-600 dark:text-rose-400' : 'text-muted-foreground'
        }`}
      >
        {title}
      </span>
      <pre className="max-h-[40vh] overflow-auto rounded-md border border-border bg-muted/40 p-3 font-mono text-xs leading-relaxed">
        {json || '—'}
      </pre>
    </div>
  );
}

function LogDetailDialog({
  row,
  onClose,
}: {
  row: ContinuousBaseUploadLogRow;
  onClose: () => void;
}) {
  const errMsg =
    row.status === 'error'
      ? (row.response?.message ??
        (typeof row.jobResponse?.jobState === 'string' ? row.jobResponse.jobState : '') ??
        'Upload failed')
      : '';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl lg:max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ActionBadge action={row.action} />
            <StatusBadge status={row.status} />
            Log #{row.id}
          </DialogTitle>
          <DialogDescription>
            Campaign #{row.campaignId} · {formatDateTime(row.createdAt)} · user {row.userId}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {errMsg && (
            <div className="rounded-md border border-rose-500/25 bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-400">
              {errMsg}
            </div>
          )}
          <div className="grid gap-4 md:grid-cols-2">
            <JsonPane title="Request metadata" json={prettyJson(row.requestData)} />
            <JsonPane
              title="Upload response"
              json={prettyJson(row.response)}
              tone={row.status === 'error' ? 'error' : undefined}
            />
          </div>
          {row.jobResponse && (
            <JsonPane title="Job status (last sync)" json={prettyJson(row.jobResponse)} />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

const CONTINUOUS_MODULE_NAME = 'Continuous Campaign';
type StatusFilter = 'all' | 'queued' | 'processing' | 'complete' | 'error';

export function ContinuousBaseLogsPage() {
  const { campaignId } = useParams<{ campaignId: string }>();
  const cid = Number(campaignId ?? 0);
  const modules = useUserModules();
  const navigate = useNavigate();

  const moduleNames = useMemo(
    () => new Set((modules.data ?? []).map((m) => m.moduleName)),
    [modules.data],
  );
  const isContinuous = moduleNames.has(CONTINUOUS_MODULE_NAME);
  const today = useMemo(() => new Date(), []);
  const [startDate, setStartDate] = useState<Date | undefined>(today);
  const [endDate, setEndDate] = useState<Date | undefined>(today);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [viewing, setViewing] = useState<ContinuousBaseUploadLogRow | null>(null);

  useEffect(() => {
    if (modules.isLoading) return;
    if (!isContinuous) {
      toast.error('Continuous Campaign module is not enabled for your account.');
      if (window.history.length > 1) navigate(-1);
      else navigate('/voice-campaign-summary');
    }
  }, [modules.isLoading, isContinuous, navigate]);

  const logs = useContinuousBaseUploadLogs(cid || null, {
    startDate: startDate ? formatDate(startDate) : undefined,
    endDate: endDate ? formatDate(endDate) : undefined,
    status: statusFilter === 'all' ? undefined : statusFilter,
  });

  const rows = useMemo(() => [...(logs.data ?? [])].sort((a, b) => b.id - a.id), [logs.data]);

  const activeCount = useMemo(
    () =>
      (logs.data ?? []).filter((r) => r.status === 'queued' || r.status === 'processing').length,
    [logs.data],
  );

  const columns = useMemo<ColumnDef<ContinuousBaseUploadLogRow>[]>(
    () => [
      {
        accessorKey: 'id',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">{row.original.id}</span>
        ),
      },
      {
        accessorKey: 'action',
        header: 'Action',
        size: 150,
        cell: ({ row }) => <ActionBadge action={row.original.action} />,
      },
      {
        id: 'base',
        header: 'Base',
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2">
            <span className="truncate font-medium text-foreground">
              {row.original.baseName ?? '—'}
            </span>
            {row.original.baseId != null && (
              <span className="font-mono text-xs text-muted-foreground">
                #{row.original.baseId}
              </span>
            )}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        size: 130,
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        id: 'numbers',
        header: 'Numbers',
        size: 150,
        cell: ({ row }) => {
          const total = row.original.response?.totalNumbers;
          const rejected = row.original.response?.rejectedNumbers;
          if (total == null && rejected == null) {
            return <span className="text-xs text-muted-foreground">—</span>;
          }
          return (
            <span className="text-xs">
              {total != null && `${Number(total).toLocaleString()} numbers`}
              {total != null && rejected != null && ' · '}
              {rejected != null && Number(rejected) > 0 && (
                <span className="text-destructive">
                  {Number(rejected).toLocaleString()} rejected
                </span>
              )}
            </span>
          );
        },
      },
      {
        accessorKey: 'jobId',
        header: 'Job',
        size: 170,
        cell: ({ row }) =>
          row.original.jobId ? (
            <span
              className="block truncate font-mono text-xs text-muted-foreground"
              title={row.original.jobId}
            >
              {row.original.jobId}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        accessorKey: 'createdAt',
        header: 'When',
        size: 180,
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        id: 'details',
        header: 'Details',
        size: 100,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <Button size="sm" variant="outline" onClick={() => setViewing(row.original)}>
            <Code className="h-3.5 w-3.5" />
            View
          </Button>
        ),
      },
    ],
    [],
  );

  if (modules.isLoading) {
    return <PageLoader />;
  }
  // Module missing → the effect above is already navigating away;
  // render nothing so there's no flash of the page.
  if (!isContinuous) {
    return null;
  }

  if (!cid) {
    return (
      <FillPage>
        <PageHeader title="Base upload logs" description="Invalid campaign." />
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        title="Base Upload Logs"
        description={`Upload history for campaign #${cid}. Rows still processing refresh automatically until they complete or fail.`}
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="ghost">
              <Link to="/voice-campaign-summary">
                <ArrowLeft className="h-4 w-4" />
                Back
              </Link>
            </Button>
            <Button variant="outline" onClick={() => logs.refetch()} disabled={logs.isFetching}>
              <RefreshCw className={logs.isFetching ? 'h-4 w-4 animate-spin' : 'h-4 w-4'} />
              Refresh
            </Button>
          </div>
        }
      />

      <div className="flex min-h-0 flex-1 flex-col gap-3">
        <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3 shadow-sm">
          <div className="space-y-1.5">
            <Label className="text-xs">Start date</Label>
            <DatePicker value={startDate} onChange={setStartDate} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">End date</Label>
            <DatePicker value={endDate} onChange={setEndDate} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Status</Label>
            <Select value={statusFilter} onValueChange={(v) => setStatusFilter(v as StatusFilter)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="queued">Queued</SelectItem>
                <SelectItem value="processing">Processing</SelectItem>
                <SelectItem value="complete">Complete</SelectItem>
                <SelectItem value="error">Error</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <span className="ml-auto flex items-center gap-3 text-xs text-muted-foreground">
            {activeCount > 0 && (
              <span className="flex items-center gap-1.5 text-amber-600 dark:text-amber-400">
                <Loader2 className="h-3 w-3 animate-spin" />
                {activeCount} processing — auto-refreshing
              </span>
            )}
            {rows.length.toLocaleString()} {rows.length === 1 ? 'log entry' : 'log entries'}
          </span>
        </div>

        <div className="min-h-0 flex-1">
          <DataTable<ContinuousBaseUploadLogRow, unknown>
            tableId="continuous-base-logs:v2"
            columns={columns}
            data={rows}
            isLoading={logs.isLoading}
            searchPlaceholder="Search logs…"
            emptyTitle="No base uploads yet"
            emptyDescription="Uploads made from this campaign's Base Upload action in this date range will appear here."
            getRowId={(row) => String(row.id)}
            initialColumnPinning={{ right: ['details'] }}
            exportFilename={`continuous-base-logs-${cid}.csv`}
            pageSize={25}
          />
        </div>
      </div>

      {viewing && (
        <LogDetailDialog key={viewing.id} row={viewing} onClose={() => setViewing(null)} />
      )}
    </FillPage>
  );
}
